import React, { useState } from "react";
import PropTypes from "prop-types";
import { useNavigate, useLocation, useParams } from "react-router";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";

import { usePermissions } from "../../../contexts/permissions";
import useQuery from "../../../hooks/useQuery";
import useQueryParams from "../../../hooks/useQueryParams";
import AddToInventoryRequest from "./AddToInventoryRequest";
import AddToInternalWorkOrder from "./AddToInternalWorkOrder";
import AddToMyOrders from "./AddToMyOrders";
import AddToCart from "./AddToCart";
import AddToKit from "./AddToKit";
import AssetCardTitle from "./AssetCardTitle";
import KitVariantsModal from "./KitVariantsModal";
import useAuth from "../../../hooks/useAuth";
import AssetCardImage from "./AssetCardImage";
import { getCostCenterValue } from "../../../views/capture/FieldsIdentifiers";
import { ProjectProvider } from "../../../contexts/project";
import ItemIdAssetModal from "../Orders/ItemIdAssetModal";
import StoreCardBody from "./StoreCardBody";

const styles = {
  card: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
    borderRadius: 2,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    overflow: "hidden",
    boxShadow: "none",
    border: "none",
    backgroundColor: "white",
    cursor: "pointer",
    "&:hover": {
      transform: "translateY(-2px)",
      backgroundColor: "action.hover",
      boxShadow: 2,
    },
  },
};

const formatProjectName = projectName => {
  if (!projectName) return projectName;

  const parenIndex = projectName.indexOf("(");
  if (parenIndex > 0 && parenIndex < projectName.length - 1) {
    const beforeParen = projectName.substring(0, parenIndex).trim();
    const afterParen = projectName.substring(parenIndex).trim();
    return (
      <>
        {beforeParen}
        <br />
        {afterParen}
      </>
    );
  }

  if (projectName.length > 40) {
    const breakPoint = projectName.lastIndexOf(" ", 40);
    if (breakPoint > 20) {
      const firstLine = projectName.substring(0, breakPoint);
      const secondLine = projectName.substring(breakPoint + 1);
      return (
        <>
          {firstLine}
          <br />
          {secondLine}
        </>
      );
    }
  }

  return projectName;
};

const AssetCard = ({
  asset,
  display,
  defaultValues = {},
  onClickImage,
  processNewItem,
  onSuccess,
  editableKitDetails = false,
  warehouses = [],
}) => {
  const { user } = useAuth();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = useQueryParams();
  const query = useQuery();
  const isShoppingOrder = location.pathname.includes("shopping/orders");
  const isInventoryRequest = query.get("orderPicking") || query.get("inventoryRequestId");
  const isKitting = location.pathname.includes("kitting") || query.get("tab") === "kitting";
  const { settings } = user.company;
  const [variantsOpen, setVariantsOpen] = useState(false);
  const detailsAssetId = queryParams.get("assetDetailsId");
  const detailsOpen = detailsAssetId === String(asset.id);
  const { projectId } = useParams();
  const displayClientProject = !isKitting && !projectId;
  const displayWarehouse = !isKitting && (!projectId || warehouses.length > 1);
  const displayKitVariants =
    asset?.type === "Kit" && asset?.availableQuantity === 0 && asset?.hasVariants;
  const currentWarehouse =
    asset.warehousesFromRackLocations?.map(warehouse => warehouse.name).join(", ") || "";
  const orderedFieldKeys = asset.project?.storeCardFields?.ordered_fields || [];
  const orderedStoreCardFieldKeys = orderedFieldKeys.filter(
    fieldKey => fieldKey !== "available_quantity"
  );

  const handleOpenDetailsFromText = e => {
    const infoButton = e.currentTarget
      .closest('[data-testid="asset-card"]')
      .querySelector('[data-testid="asset-info-button"]');

    if (infoButton) {
      infoButton.click();
    }
  };

  const handleToggleDetails = () => {
    const nextParams = new URLSearchParams(location.search);

    if (detailsOpen) {
      nextParams.delete("assetDetailsId");
    } else {
      nextParams.set("assetDetailsId", String(asset.id));
    }

    navigate({ pathname: location.pathname, search: nextParams.toString() });
  };

  const handleCardClick = e => {
    // Ignore clicks that originated from a portal-rendered dialog/menu owned by the card.
    if (e.target !== e.currentTarget && !e.currentTarget.contains(e.target)) {
      return;
    }

    const clickedInteractiveElement = e.target.closest(
      "button, input, .MuiIconButton-root, .quantity-selector, .MuiButton-root"
    );

    if (clickedInteractiveElement) {
      return;
    }
    handleToggleDetails();
  };

  const availableQuantityRow = (
    <Typography
      fontWeight={500}
      color="textPrimary"
      sx={{ cursor: "pointer" }}
      onClick={handleOpenDetailsFromText}
    >
      {asset.availableQuantity} Available
    </Typography>
  );

  return (
    <Box sx={styles.card} data-testid="asset-card" onClick={handleCardClick}>
      <AssetCardImage display={display} asset={asset} />
      <Box display="flex" flexDirection="column" gap={1} px={2} pb={2}>
        {orderedFieldKeys.length > 0 ? (
          <>
            <StoreCardBody
              asset={asset}
              orderedFieldKeys={orderedStoreCardFieldKeys}
              displayClientProject={displayClientProject}
              displayWarehouse={displayWarehouse}
              reservationsEnabled={settings.reservations}
              onOpenDetails={handleOpenDetailsFromText}
            />
            {availableQuantityRow}
          </>
        ) : (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box flex={1}>
                <AssetCardTitle display={display} asset={asset} />
              </Box>
            </Box>
            {displayClientProject && (
              <Box display="flex" flexDirection="column">
                <Typography
                  fontWeight={500}
                  color="textSecondary"
                  sx={{
                    lineHeight: 1.2,
                    wordBreak: "break-word",
                  }}
                >
                  {formatProjectName(asset.project.name)}
                </Typography>
              </Box>
            )}
            {displayWarehouse && currentWarehouse && (
              <Box display="flex">
                <Typography color="textPrimary">{currentWarehouse}</Typography>
              </Box>
            )}
            {(asset.customFields || [])
              .filter(f => f?.label?.toLowerCase().includes("owner") && f?.value)
              .map((f, i) => (
                <Typography key={i} color="textPrimary">
                  {f.value}
                </Typography>
              ))}
            {asset.costCenter && (
              <Typography
                color="textPrimary"
                sx={{ cursor: "pointer" }}
                onClick={handleOpenDetailsFromText}
              >
                {getCostCenterValue(asset.costCenter)}
              </Typography>
            )}
            {availableQuantityRow}
            {settings.reservations && Number(asset.reservedQuantity || 0) !== 0 && (
              <Typography fontWeight={400} color="textSecondary">
                {asset.reservedQuantity} Reserved
              </Typography>
            )}
          </>
        )}
        {displayKitVariants && (
          <Box display="flex">
            <Button variant="text" size="small" onClick={() => setVariantsOpen(true)}>
              View Kit Variants
            </Button>
          </Box>
        )}

        {variantsOpen && (
          <KitVariantsModal
            asset={asset}
            defaultValues={defaultValues}
            processNewItem={processNewItem}
            onClose={() => setVariantsOpen(false)}
          />
        )}
      </Box>
      {can("pages.shopping_cart") && (
        <Box>
          <Box mt="14px" display="flex" gap={1} px={2} pb={2}>
            {isKitting ? (
              <AddToKit size="small" asset={asset} defaultValues={defaultValues} />
            ) : isShoppingOrder ? (
              <AddToMyOrders
                size="small"
                asset={asset}
                defaultValues={defaultValues}
                onSuccess={onSuccess}
                processNewItem={processNewItem}
              />
            ) : isInventoryRequest ? (
              <AddToInventoryRequest
                size="small"
                asset={asset}
                defaultValues={defaultValues}
                onSuccess={onSuccess}
                processNewItem={processNewItem}
              />
            ) : query.get("internalWorkOrderId") ? (
              <AddToInternalWorkOrder
                size="small"
                asset={asset}
                defaultValues={defaultValues}
                processNewItem={processNewItem}
              />
            ) : (
              <AddToCart
                size="small"
                asset={asset}
                defaultValues={defaultValues}
                onSuccess={onSuccess}
              />
            )}
          </Box>
        </Box>
      )}

      {detailsOpen && (
        <ProjectProvider id={asset.project.id} loadingComponent={<span />}>
          <ItemIdAssetModal id={asset.id} asset={asset} onClose={handleToggleDetails} />
        </ProjectProvider>
      )}

      {variantsOpen && (
        <KitVariantsModal
          asset={asset}
          onClose={() => setVariantsOpen(false)}
          processNewItem={processNewItem}
        />
      )}
    </Box>
  );
};

AssetCard.propTypes = {
  className: PropTypes.string,
  asset: PropTypes.object.isRequired,
};

export default AssetCard;
